/**
 * «Журнал действий» карточки звонка (`?ctab=log`): что робот вызывал, с какими параметрами и что вышло —
 * старт, инструменты, действия в CRM, дата следующей обработки, конец; ниже — записи в Bitrix этого звонка
 * (`bitrix_write_log`: метод, параметры, было, ответ). Спека `docs/specs/2026-10-27-voice-deal-call-design.md`.
 */
import { useQuery } from "@tanstack/react-query";

import {
  type LabLogBitrix,
  type LabLogEvent,
  labLogQuery,
} from "@/lib/voiceLab";

const ВИД: Record<LabLogEvent["kind"], string> = {
  start: "старт",
  turn: "реплика",
  tool: "инструмент",
  crm: "CRM",
  next_work: "дата обработки",
  end: "конец",
};

function Json({ v }: { v: unknown }) {
  if (v == null) return null;
  return (
    <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2 font-mono text-[11.5px]">
      {JSON.stringify(v, null, 2)}
    </pre>
  );
}

function коротко(r: Record<string, unknown> | null): string {
  if (!r) return "";
  const t =
    r["text"] ??
    (r["status"] != null
      ? `${String(r["status"])}: ${String(r["plan"] ?? "")}`
      : null);
  return t == null ? "" : String(t).slice(0, 220);
}

const КТО: Record<string, string> = { agent: "робот", user: "клиентка" };

function Событие({ e }: { e: LabLogEvent }) {
  if (e.kind === "turn")
    return (
      <div className="border-b border-border py-1.5">
        <span className="font-mono text-fg-faint">{e.date.slice(11, 19)}</span>{" "}
        <b className={e.name === "agent" ? "text-accent" : ""}>{КТО[e.name] ?? e.name}:</b>{" "}
        {String(e.result?.["text"] ?? "")}
      </div>
    );
  return (
    <details className="border-b border-border py-1.5">
      <summary className="cursor-pointer">
        <span className="font-mono text-fg-faint">{e.date.slice(11, 19)}</span>{" "}
        <span className="text-fg-muted">{ВИД[e.kind] ?? e.kind}</span>{" "}
        <b>{e.name}</b>
        {e.ms != null && <span className="text-fg-faint"> · {e.ms} мс</span>}
        {e.error ? (
          <span className="text-err"> · {e.error.slice(0, 160)}</span>
        ) : (
          <span className="text-fg-muted"> · {коротко(e.result)}</span>
        )}
      </summary>
      <div className="grid gap-1 pl-4 sm:grid-cols-2">
        <div>
          <div className="text-fg-faint">передали</div>
          <Json v={e.params} />
        </div>
        <div>
          <div className="text-fg-faint">результат</div>
          <Json v={e.result} />
        </div>
      </div>
    </details>
  );
}

function Запись({ b }: { b: LabLogBitrix }) {
  return (
    <details className="border-b border-border py-1.5">
      <summary className="cursor-pointer">
        <b>{b.method}</b> {b.entity} {b.entity_id}
        {b.error ? (
          <span className="text-err"> · {b.error.slice(0, 160)}</span>
        ) : (
          <span className="text-ok"> · ок</span>
        )}
        <span className="text-fg-muted"> · {b.reason}</span>
      </summary>
      <div className="grid gap-1 pl-4 sm:grid-cols-3">
        <div>
          <div className="text-fg-faint">параметры</div>
          <Json v={b.params} />
        </div>
        <div>
          <div className="text-fg-faint">было</div>
          <Json v={b.before} />
        </div>
        <div>
          <div className="text-fg-faint">ответ Bitrix</div>
          <Json v={b.result} />
        </div>
      </div>
    </details>
  );
}

export function VoiceLabCallLog({ room }: { room: string }) {
  const q = useQuery(labLogQuery(room));
  if (q.isError)
    return (
      <div className="text-[12.5px] text-err">
        Журнал действий не загрузился
      </div>
    );
  if (!q.data)
    return <div className="text-[12.5px] text-fg-muted">Загружаю…</div>;
  const { events, bitrix } = q.data;
  return (
    <section
      className="flex max-w-4xl flex-col gap-3 text-[12.5px]"
      data-annot="журнал действий звонка"
    >
      {!events.length && (
        <div className="text-fg-muted">
          Для этого звонка журнала действий нет — он ведётся с 27.10.26.
        </div>
      )}
      {!!events.length && (
        <div>
          {events.map((e) => (
            <Событие key={e.id} e={e} />
          ))}
        </div>
      )}
      <div className="font-medium">Записи в Bitrix · {bitrix.length}</div>
      {bitrix.length ? (
        bitrix.map((b) => <Запись key={b.id} b={b} />)
      ) : (
        <div className="text-fg-muted">Робот в Bitrix не писал</div>
      )}
    </section>
  );
}
